import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FolderOpen, FolderSync, Loader2, Save } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { fmtFull } from "../lib/format";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";

const bridge = () => window.mufaDesktop || {};

export const FolderBackup = () => {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState("");
  useEffect(() => { api.get("/desktop/backup").then(({ data }) => setF({ keep: 30, ...data })); }, []);
  if (!f) return <Loader2 className="h-5 w-5 animate-spin" />;

  const save = async (next) => {
    setBusy("save");
    try {
      const { data } = await api.put("/desktop/backup", { enabled: next.enabled, folder: next.folder, keep: Number(next.keep) || 30 });
      setF(data);
      toast.success(data.last_error ? `Disimpan, namun: ${data.last_error}` : "Pengaturan backup disimpan");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };
  const pick = async () => {
    const p = await bridge().pickFolder?.();
    if (p) save({ ...f, folder: p, enabled: true });
  };
  const run = async () => {
    setBusy("run");
    try {
      const { data } = await api.post("/desktop/backup/run");
      setF(data.state);
      toast.success(`Backup tersimpan: ${data.file}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="space-y-4" data-testid="folder-backup">
      <div className="space-y-1.5">
        <Label>Folder tujuan</Label>
        <div className="flex gap-2">
          <Input value={f.folder || ""} onChange={(e) => setF({ ...f, folder: e.target.value })} placeholder="Contoh: D:\Backup MUFA" data-testid="folder-backup-path" />
          <Button type="button" onClick={pick} disabled={!!busy || !bridge().pickFolder} data-testid="folder-backup-pick-btn"><FolderOpen className="h-4 w-4 mr-2" />Pilih Folder</Button>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4 items-end">
        <label className="flex items-center justify-between text-sm rounded-lg border px-3 py-2.5">
          <span>Backup otomatis aktif</span>
          <Switch checked={!!f.enabled} onCheckedChange={(v) => save({ ...f, enabled: v })} disabled={!!busy} data-testid="folder-backup-enabled-switch" />
        </label>
        <div className="space-y-1.5"><Label>Simpan file terakhir</Label>
          <Input type="number" min={1} max={365} value={f.keep} onChange={(e) => setF({ ...f, keep: e.target.value })} data-testid="folder-backup-keep-input" /></div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={() => save(f)} disabled={!!busy} data-testid="folder-backup-save-btn">{busy === "save" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Simpan</Button>
        <Button onClick={run} disabled={!!busy || !f.folder} data-testid="folder-backup-run-btn">{busy === "run" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FolderSync className="h-4 w-4 mr-2" />}Backup Sekarang</Button>
        {f.folder && bridge().openFolder && <Button variant="ghost" onClick={() => bridge().openFolder(f.folder)} data-testid="folder-backup-open-btn">Buka Folder</Button>}
      </div>
      <p className="text-xs text-muted-foreground" data-testid="folder-backup-last">
        {f.last_backup_at ? `Terakhir: ${fmtFull(f.last_backup_at)} (${f.last_reason}) · ${f.last_file}` : "Belum ada backup ke folder."}
      </p>
      {f.last_error && <p className="text-xs text-destructive" data-testid="folder-backup-error">{f.last_error}</p>}
      <p className="text-xs text-muted-foreground">Berjalan otomatis setiap tutup shift dan sekali sehari saat aplikasi dibuka. File lama di luar jumlah di atas dihapus otomatis.</p>
    </div>
  );
};
